import {
  DICIONARIOS,
  IDIOMAS,
  definirIdioma,
  idiomaAtual,
  t,
  type Chave,
  type Idioma,
} from './index'
import { esc } from '@/ui/esc'

/**
 * O seletor de idioma, e o que traduz HTML estático.
 *
 * É o único módulo do i18n que toca o DOM. Fica separado para o resto
 * continuar importável de worker e de teste.
 */

/**
 * O seletor, como HTML.
 *
 * Um `<select>` nativo: dois idiomas não justificam um menu próprio, e o
 * nativo já é acessível por teclado e leitor de tela. Discreto de propósito —
 * quem precisa acha; quem não precisa não repara.
 *
 * Sem `w-full`: a regra global de `select` não existe, mas a de `button` dá
 * largura cheia a tudo e é bom não depender do que o `.select-modo` faz.
 */
export function seletorDeIdiomaHtml(classes = ''): string {
  const atual = idiomaAtual()
  const opcoes = IDIOMAS.map(
    (id) =>
      `<option value="${id}" ${id === atual ? 'selected' : ''}>${esc(nomeDoIdioma(id))}</option>`,
  ).join('')

  return `<select data-idioma aria-label="${esc(t('idioma.rotulo'))}" title="${esc(t('idioma.rotulo'))}"
    class="cursor-pointer appearance-none rounded-md border border-line bg-transparent px-2 py-1 font-mono text-[11px] text-text-dim hover:text-text ${classes}">${opcoes}</select>`
}

/** O nome de um idioma, escrito NELE — é assim que quem o procura o reconhece. */
export function nomeDoIdioma(idioma: Idioma): string {
  return DICIONARIOS[idioma][`idioma.${idioma}` as Chave] as string
}

/**
 * Faz o seletor funcionar.
 *
 * Trocar o idioma RECARREGA a página. Cada tela deste site é um `innerHTML`
 * montado a partir de estado, e muita string é calculada uma vez ao carregar
 * (a barra de navegação, os rótulos do canvas). Redesenhar tudo sem recarregar
 * exigiria que cada página soubesse se redesenhar — e a que não soubesse
 * ficaria metade em cada língua. Recarregar é honesto e custa um segundo.
 */
export function ligarSeletorDeIdioma(raiz: ParentNode = document): void {
  raiz.querySelectorAll<HTMLSelectElement>('select[data-idioma]').forEach((sel) => {
    sel.addEventListener('change', () => {
      const escolhido = sel.value as Idioma
      if (!(IDIOMAS as readonly string[]).includes(escolhido)) return
      definirIdioma(escolhido)
      location.reload()
    })
  })
}

/**
 * Traduz o HTML estático de uma página.
 *
 * `data-i18n="chave"` troca o `textContent`; `data-i18n-html="chave"` troca o
 * `innerHTML` — só para texto do DICIONÁRIO, que é nosso, nunca para dado de
 * fora. Existe porque as páginas `.html` têm títulos e cabeçalhos fixos que
 * nenhum módulo redesenha.
 *
 * Chave desconhecida é deixada em paz: o HTML continua mostrando o que tinha,
 * e o typecheck não alcança atributos de HTML — o teste de chaves cobre o
 * dicionário, e este aviso cobre o atributo.
 */
export function traduzirDocumento(raiz: ParentNode = document): void {
  const dicionario = DICIONARIOS[idiomaAtual()]

  raiz.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const chave = el.dataset.i18n as Chave
    if (chave in dicionario) el.textContent = t(chave as never)
  })
  raiz.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    const chave = el.dataset.i18nHtml as Chave
    if (chave in dicionario) el.innerHTML = t(chave as never)
  })
}

/**
 * Marca o documento com o idioma em vigor.
 *
 * O `lang` do `<html>` vem fixo em `pt-BR` dos arquivos `.html`; sem isto o
 * leitor de tela pronunciaria inglês com sotaque de português, e a tradução
 * automática do navegador ofereceria traduzir uma página que já está na
 * língua do leitor.
 */
export function aplicarIdiomaNoDocumento(): void {
  document.documentElement.lang = idiomaAtual()
}
